// Pure helpers behind the phrase-rule page: option lists, labels, the form
// and payload shapes of GET/POST/PATCH /:org/moderation/phrase-rules, and the
// result of POST /:org/moderation/test. Nothing here fetches or renders.
import { formatRegulationName } from '../../utils/regulationLabels'

const SENTENCE_PUNCTUATION_PATTERN = /[.!?]$/

// Bounds the server enforces (moderation.MaxPhraseRuleLength, the note
// column check); the form mirrors them so a save is not refused for length.
export const MAX_PHRASE_LENGTH = 256
export const MAX_NOTE_LENGTH = 1000

export const MATCH_KIND_OPTIONS = [
  { value: 'word', label: 'Whole words', description: 'Matches whole words, also when written with lookalike letters, numbers for letters, repeated or spaced-out letters, and with a dash, an emoji or nothing between them.' },
  { value: 'substring', label: 'Anywhere', description: 'Matches the phrase anywhere, also inside longer words and through lookalike spellings.' },
  { value: 'regex', label: 'Pattern', description: 'Matches a regular expression (RE2 syntax), ignoring case.' },
]

export const ACTION_OPTIONS = [
  { value: 'block', label: 'Block', description: 'A post that contains the phrase cannot be saved.' },
  { value: 'flag', label: 'Flag', description: 'A post that contains the phrase is held in the moderation queue for a brand admin.' },
]

const AUTO_REJECT_FLAG_DESCRIPTION = 'A post that contains the phrase is rejected automatically; a brand admin can overturn it.'

// actionOptions words the actions for the brand's state from
// GET /:org/moderation/status: on a brand that rejects flagged content, a
// flag rejects the post rather than holding it for review.
export function actionOptions(moderationStatus) {
  if (moderationStatus?.flagged_content !== 'auto_reject') return ACTION_OPTIONS
  return ACTION_OPTIONS.map((option) => (option.value === 'flag' ? { ...option, description: AUTO_REJECT_FLAG_DESCRIPTION } : option))
}

const CATEGORY_LABELS = {
  restricted_phrase: 'Restricted phrase',
  profanity: 'Profanity',
}

export const RULE_FILTERS = [
  { value: 'brand', label: 'Brand rules' },
  { value: 'platform', label: 'Platform rules' },
  { value: 'all', label: 'All rules' },
]

const optionLabel = (options, value) => options.find((option) => option.value === value)?.label || value || ''

export function matchKindLabel(kind) {
  return optionLabel(MATCH_KIND_OPTIONS, kind)
}

export function actionLabel(action) {
  return optionLabel(ACTION_OPTIONS, action)
}

export function actionTone(action) {
  return action === 'block' ? 'error' : 'warning'
}

export function categoryLabel(category) {
  return CATEGORY_LABELS[category] || String(category || '').replaceAll('_', ' ')
}

// ruleRegulationLabel names the regulation a rule is scoped to; a rule with
// no regulation applies to every one.
export function ruleRegulationLabel(code) {
  return code ? formatRegulationName(code) || code : 'All'
}

// regulationOptions lists the regulations a rule may be scoped to, plus the
// rule's own regulation when the organization no longer routes to it, so an
// edit never changes the scope silently.
export function regulationOptions(regulations = [], current = '') {
  const codes = [...new Set([...(regulations || []), ...(current ? [current] : [])])]
  const options = codes
    .map((code) => ({ value: code, label: `${formatRegulationName(code) || code} (${code})` }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
  return [{ value: '', label: 'All regulations' }, ...options]
}

export function filterRules(rules = [], filter = 'brand') {
  if (filter === 'brand') return rules.filter((rule) => !rule.read_only)
  if (filter === 'platform') return rules.filter((rule) => rule.read_only)
  return rules
}

// hasPlatformRules reports whether the list carries Wager's read-only
// platform rules. The API sends them to internal admins only, so a brand
// admin's list never does.
export function hasPlatformRules(rules = []) {
  return (rules || []).some((rule) => rule.read_only)
}

export const EMPTY_RULE_FORM = Object.freeze({
  phrase: '',
  match_kind: 'word',
  action: 'block',
  regulation_code: '',
  note: '',
  active: true,
  // confirm_broad saves a rule that matches much ordinary content.
  confirm_broad: false,
})

export function ruleForm(rule) {
  if (!rule) return { ...EMPTY_RULE_FORM }
  return {
    phrase: rule.phrase || '',
    match_kind: rule.match_kind || EMPTY_RULE_FORM.match_kind,
    action: rule.action || EMPTY_RULE_FORM.action,
    regulation_code: rule.regulation_code || '',
    note: rule.note || '',
    active: rule.active !== false,
    confirm_broad: false,
  }
}

const codePointLength = (value) => Array.from(value || '').length
// The server bounds a note in bytes.
const byteLength = (value) => new TextEncoder().encode(value || '').length

// ruleFormProblems names, per field, what the server would refuse before
// it is sent. A blank phrase is not listed: the form simply cannot save yet.
export function ruleFormProblems(form) {
  const problems = {}
  if (codePointLength((form?.phrase || '').trim()) > MAX_PHRASE_LENGTH) {
    problems.phrase = `A phrase is at most ${MAX_PHRASE_LENGTH} characters.`
  }
  if (byteLength((form?.note || '').trim()) > MAX_NOTE_LENGTH) {
    problems.note = `A note is at most ${MAX_NOTE_LENGTH} characters.`
  }
  return problems
}

export function ruleFormReady(form) {
  return (form?.phrase || '').trim() !== '' && Object.keys(ruleFormProblems(form)).length === 0
}

// rulePayloadWithoutConfirm is the rule as the form would save it, without
// the too-broad confirmation: the preview body's rule.
function rulePayloadWithoutConfirm(form) {
  return {
    phrase: (form.phrase || '').trim(),
    match_kind: form.match_kind,
    action: form.action,
    regulation_code: form.regulation_code || '',
    note: (form.note || '').trim(),
    active: Boolean(form.active),
  }
}

// rulePayload is the create body. regulation_code '' means every regulation;
// the server reads '' the same way on create and clears the scope on edit.
// confirm_broad is sent only once the admin confirmed a too-broad rule.
export function rulePayload(form) {
  const payload = rulePayloadWithoutConfirm(form)
  return form.confirm_broad ? { ...payload, confirm_broad: true } : payload
}

// ruleUpdatePayload is the edit body: every field, guarded by the version the
// admin opened, so a rule someone else changed is refused rather than
// overwritten.
export function ruleUpdatePayload(rule, form) {
  return { ...rulePayload(form), expected_version: rule.version }
}

// previewRequest is the body of POST /:org/moderation/phrase-rules/preview
// for the change the dialog would save: mode is 'create', 'edit' or
// 'delete'.
export function previewRequest(mode, rule, form) {
  if (mode === 'delete') return { operation: 'delete', rule_id: rule.id }
  if (mode === 'edit') return { operation: 'update', rule_id: rule.id, rule: rulePayloadWithoutConfirm(form) }
  return { operation: 'create', rule: rulePayloadWithoutConfirm(form) }
}

// measuredChange mirrors the server's rule for which writes it measures for
// breadth: a new active rule, a rule turned on, or a new phrase or match
// kind. A note or regulation edit, or turning a rule off, is not measured.
export function measuredChange(rule, form) {
  if (!form?.active) return false
  if (!rule) return true
  return !rule.active || (form.phrase || '').trim() !== rule.phrase || form.match_kind !== rule.match_kind
}

export const TOO_BROAD_MESSAGE = 'This rule matches a lot of ordinary content. Check what it matches and confirm to save it.'

// breadthSentence says how much ordinary content a rule matches on its own:
// the brand's newest posts and the built-in ordinary sports sentences.
export function breadthSentence(breadth) {
  const corpus = `${breadth.corpus_matched} of ${breadth.corpus_checked} ordinary sports sentences`
  if (breadth.recent_posts_checked > 0) {
    return `Matches ${breadth.recent_posts_matched} of this brand's last ${breadth.recent_posts_checked} posts and ${corpus}.`
  }
  return `Matches ${corpus}. This brand has no posts yet.`
}

// tooBroadWarning says what a too-broad rule would do to ordinary posts:
// a block stops saves; a flag holds them, or rejects them on a brand that
// rejects flagged content.
export function tooBroadWarning(action, flaggedContent) {
  if (action !== 'flag') return 'This rule would stop many ordinary posts from being saved.'
  return flaggedContent === 'auto_reject'
    ? 'This rule would reject many ordinary posts automatically.'
    : 'This rule would send many ordinary posts to the moderation queue.'
}

export const PREVIEW_LOADING = 'Working out what this change does…'
export const PREVIEW_FAILED = 'What this change does could not be worked out. You can still save it.'

const posts = (count, singular, plural) => `${count} ${count === 1 ? singular : plural}`

// previewLines says what a rule change does now, from the preview answer:
// first how much ordinary content the rule matches (only for a measured
// change, which the server measured too), then what happens to the brand's
// held and rule-rejected posts. It is a prediction at the moment the preview
// ran; the save is never held back by it.
export function previewLines(preview, { measured = false } = {}) {
  if (!preview) return []
  const lines = measured && preview.corpus_checked > 0 ? [breadthSentence(preview)] : []
  if (!preview.enforcing) return [...lines, 'Moderation is not enforced right now, so no held post changes when you save.']
  if (!preview.org_enabled) {
    lines.push('Moderation is off for this brand. Posts waiting in its moderation queue still follow this change; posts its rules rejected are checked again once moderation is on.')
  }
  if (preview.held_released > 0) {
    lines.push(`${posts(preview.held_released, 'held post', 'held posts')} will be released and go live.`)
  }
  if (preview.queued_rejected > 0) {
    lines.push(`${posts(preview.queued_rejected, 'post', 'posts')} waiting in the moderation queue will be rejected.`)
  }
  if (preview.rejected_rechecked > 0) {
    lines.push(`${posts(preview.rejected_rechecked, 'post', 'posts')} the brand's rules rejected will be checked again; each goes live or to the queue unless something else stops it.`)
  }
  if (!(preview.held_released > 0) && !(preview.queued_rejected > 0) && !(preview.rejected_rechecked > 0)) {
    lines.push('No held post changes when you save.')
  }
  if (preview.capped) lines.push('Counted for the first 200 held posts; more may change.')
  return lines
}

function sentence(message) {
  const text = String(message || '').trim()
  if (!text) return ''
  const capitalised = text.charAt(0).toUpperCase() + text.slice(1)
  return SENTENCE_PUNCTUATION_PATTERN.test(capitalised) ? capitalised : `${capitalised}.`
}

export const RULE_CONFLICT_MESSAGE = 'This rule changed since you opened it. Reload it to see the latest version, then make your change again.'
export const RULE_MISSING_MESSAGE = 'This rule no longer exists. Someone may have deleted it.'

// classifyRuleError maps a refused rule write onto what the page says.
// conflict and missing mean the list is out of date and should be reloaded.
export function classifyRuleError(error, fallback = 'The rule could not be saved.') {
  const status = error?.response?.status
  const data = error?.response?.data || {}
  if (status === 409 && data.code === 'phrase_rule_too_broad') {
    return {
      kind: 'too_broad',
      message: TOO_BROAD_MESSAGE,
      breadth: {
        recent_posts_checked: data.recent_posts_checked,
        recent_posts_matched: data.recent_posts_matched,
        corpus_checked: data.corpus_checked,
        corpus_matched: data.corpus_matched,
      },
    }
  }
  if (status === 409 && data.code === 'phrase_rule_version_conflict') {
    return { kind: 'conflict', message: RULE_CONFLICT_MESSAGE }
  }
  if (status === 409 && data.code === 'phrase_rule_exists') {
    return { kind: 'exists', message: sentence(data.error) || 'An identical phrase rule already exists for this regulation.' }
  }
  if (status === 400 && data.code === 'invalid_phrase_rule') {
    return { kind: 'invalid', message: sentence(data.error) || 'Check the rule and try again.' }
  }
  if (status === 404) {
    return { kind: 'missing', message: RULE_MISSING_MESSAGE }
  }
  return { kind: 'error', message: sentence(data.error) || error?.message || fallback }
}

// Without the brand's status (loading or failed) the wording holds either way.
const TEST_STATUS_VIEWS = {
  passed: {
    tone: 'success',
    label: 'No rule matched',
    description: 'No rule matched.',
  },
  flagged: {
    tone: 'warning',
    label: 'Flagged',
    description: 'Flagged: a post with this text goes to the moderation queue when moderation is on for this brand.',
  },
  rejected: {
    tone: 'error',
    label: 'Blocked',
    description: 'Blocked: this text is refused when moderation is on for this brand.',
  },
}

// testStatusDescription says what a result means for a post on the brand
// as it is now: enforcing (and what a flag does there) or not.
function testStatusDescription(status, moderationStatus) {
  const enforcing = Boolean(moderationStatus.enforcing)
  if (status === 'passed') {
    return enforcing ? 'No rule matched. The moderation provider still checks the post when it is saved.' : 'No rule matched.'
  }
  if (status === 'flagged') {
    if (!enforcing) return 'Flagged: a post with this text would go to the moderation queue once moderation is on for this brand.'
    return moderationStatus.flagged_content === 'auto_reject'
      ? 'Flagged: a post with this text is rejected automatically. A brand admin can overturn it.'
      : 'Flagged: a post with this text goes to the moderation queue.'
  }
  return enforcing
    ? 'Blocked: a post with this text cannot be saved.'
    : 'Blocked: a post with this text would be refused once moderation is on for this brand.'
}

// testStatusView labels a tester result. moderationStatus is the brand's
// state from GET /:org/moderation/status, or missing while it loads.
export function testStatusView(status, moderationStatus) {
  const view = TEST_STATUS_VIEWS[status]
  if (!view) return { tone: 'default', label: status || 'Unknown result', description: '' }
  if (!moderationStatus) return view
  return { ...view, description: testStatusDescription(status, moderationStatus) }
}

// TEST_FIELDS are the fields the tester sends, in the order it shows them.
// Findings name the field their offsets count into.
export const TEST_FIELDS = [
  { field: 'title', label: 'Title' },
  { field: 'text', label: 'Text' },
]

// testRequest sends the text exactly as typed: finding offsets count into
// it, so trimming would shift every highlight.
export function testRequest(input) {
  const request = {}
  TEST_FIELDS.forEach(({ field }) => {
    if (typeof input?.[field] === 'string' && input[field] !== '') request[field] = input[field]
  })
  return request
}

// ruleForFinding finds the rule a phrase finding's source names
// ("<rule id>@<version>").
export function ruleForFinding(finding, rules = []) {
  const source = String(finding?.source || '')
  const at = source.lastIndexOf('@')
  const id = at > 0 ? source.slice(0, at) : source
  return id ? rules.find((rule) => rule.id === id) || null : null
}
